import { useMemo } from 'react';

import { LayoutCard, MetricsBox, Stack } from '@cast/design-system';
import { useClusterDashboardTimeRange } from '@cast/features-cluster-shared';
import { useInViewport } from '@cast/shared-layout-ui';
import {
  useOomKillEventMetricsChartData,
  type OomKillBucketQueryRange,
} from '@cast/split-me/features/cluster/dashboard/WorkloadEventMetricsSection';
import { dashesOrNum, toNumberWithSeparators } from '@cast/utils';

import { clusterDashboardChartColors } from '../_constants/dashboardTokens';
import {
  ChartSummaryMetric,
  ChartSummaryMetricsRow,
} from './shared/ChartSummaryMetricsRow';
import { ClusterDashboardRangePicker } from './shared/ClusterDashboardRangePicker';
import { DashboardChartFrame } from './shared/DashboardChartFrame';
import { DashboardSectionError } from './shared/DashboardSectionError';
import {
  ChartSummaryMetricSkeleton,
  ChartSummaryMetricsRowSkeleton,
} from './shared/DashboardMetricsSkeleton';
import { OomKillsChart } from './shared/OomKillsChart';

const formatCount = (value: number | null) =>
  dashesOrNum({
    value,
    mapNumber: (num) => toNumberWithSeparators(num) || '0',
  });

export const OomKillsSection = () => {
  const inView = useInViewport();
  const { timeZone, key, overviewQueryArgs } = useClusterDashboardTimeRange();

  const rangeSelection = useMemo<OomKillBucketQueryRange>(
    () => ({
      getRange: () => ({
        from: overviewQueryArgs.startTime,
        to: overviewQueryArgs.endTime,
      }),
      key,
      stepSeconds: overviewQueryArgs.stepSeconds,
    }),
    [overviewQueryArgs, key]
  );

  const { chartData, totals, isLoading, isError, refetch } =
    useOomKillEventMetricsChartData({
      rangeSelection,
      enabled: inView,
    });

  return (
    <LayoutCard
      title="Cluster OOM kills"
      headerEndContent={<ClusterDashboardRangePicker />}
      testId="oom-kills-section"
    >
      <DashboardSectionError hasError={isError} onRetry={refetch}>
        <Stack gap={16}>
          {isLoading ? (
            <ChartSummaryMetricsRowSkeleton alwaysRow>
              <ChartSummaryMetricSkeleton />
              <ChartSummaryMetricSkeleton />
              <ChartSummaryMetricSkeleton />
            </ChartSummaryMetricsRowSkeleton>
          ) : (
            <ChartSummaryMetricsRow alwaysRow>
              <MetricsBox
                size="l"
                slots={{
                  label: 'Total events',
                  value: String(formatCount(totals.total)),
                }}
              />
              <ChartSummaryMetric
                color={clusterDashboardChartColors.redSubtle}
                label="Unmanaged workloads"
                infoTooltip="OOMKill events for workloads with Workload Autoscaler disabled"
                value={formatCount(totals.unmanaged)}
              />
              <ChartSummaryMetric
                color={clusterDashboardChartColors.redStrong}
                label="Managed workloads"
                infoTooltip="OOMKill events for workloads with Workload Autoscaler enabled"
                value={formatCount(totals.managed)}
              />
            </ChartSummaryMetricsRow>
          )}

          <DashboardChartFrame>
            <OomKillsChart
              data={chartData}
              timeZone={timeZone}
              rangeSelection={rangeSelection}
              isLoading={isLoading}
              hasNoData={!isLoading && !chartData.length}
            />
          </DashboardChartFrame>
        </Stack>
      </DashboardSectionError>
    </LayoutCard>
  );
};
